import React from 'react';
import { Heart, HelpCircle, Flame } from 'lucide-react';
import { IntimacyData } from '../hooks/useStatsData';
import { CardSkeleton } from './CardSkeleton';
interface CardIntimacyProgressProps {
  loading: boolean;
  data: IntimacyData | null;
}
export const CardIntimacyProgress: React.FC<CardIntimacyProgressProps> = ({
  loading,
  data
}) => {
  if (loading) return <CardSkeleton data-id="element-20782" />;
  if (!data) return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm" data-id="element-20783">
        <div className="flex items-center justify-between mb-4" data-id="element-20784">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-pink-300 dark:to-purple-200" data-id="element-20785">
            <Heart className="text-pink-500 dark:text-pink-400" size={18} data-id="element-20786" />
            Intimacy Progress
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8" data-id="element-20787">
          <p className="text-gray-500 dark:text-indigo-200/70 text-sm" data-id="element-20788">
            Begin your journey to form deeper connections.
          </p>
          <button className="mt-5 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 dark:from-pink-600 dark:to-purple-600 text-white rounded-full text-sm font-medium border border-pink-300/30 dark:border-pink-500/30 shadow-sm dark:shadow-[0_0_10px_rgba(236,72,153,0.3)]" data-id="element-20789">
            Begin Intimacy Journey
          </button>
        </div>
      </div>;
  const progressPercentage = data.current / data.nextLevel * 100;
  return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm relative overflow-hidden" data-id="element-20790">
      <div className="relative" data-id="element-20791">
        <div className="flex items-center justify-between mb-4" data-id="element-20792">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-pink-300 dark:to-purple-200" data-id="element-20793">
            <Heart className="text-pink-500 dark:text-pink-400" size={18} data-id="element-20794" />
            Intimacy Progress
          </h3>
        </div>
        <div className="flex items-center gap-6 mb-6" data-id="element-20795">
          <div className="relative" data-id="element-20796">
            <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-br from-pink-100/80 to-purple-100/80 dark:from-pink-900/40 dark:to-purple-900/40 border border-pink-200/50 dark:border-pink-500/30 shadow-sm dark:shadow-[0_0_15px_rgba(236,72,153,0.3)] animate-pulse-slow" data-id="element-20797">
              <span className="text-2xl font-bold text-pink-500 dark:text-pink-200" data-id="element-20798">
                {data.level}
              </span>
            </div>
            <svg className="absolute inset-0 w-20 h-20 -rotate-90" data-id="element-20799">
              <circle cx="40" cy="40" r="36" fill="none" stroke="#F9E6F0" strokeWidth="4" className="dark:opacity-30" data-id="element-20800" />
              <circle cx="40" cy="40" r="36" fill="none" stroke="url(#heart-gradient)" strokeWidth="4" strokeDasharray={2 * Math.PI * 36} strokeDashoffset={2 * Math.PI * 36 * (1 - progressPercentage / 100)} className="drop-shadow-[0_0_3px_rgba(236,72,153,0.5)]" data-id="element-20801" />
              <defs data-id="element-20802">
                <linearGradient id="heart-gradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-20803">
                  <stop offset="0%" stopColor="#EC4899" data-id="element-20804" />
                  <stop offset="100%" stopColor="#8B5CF6" data-id="element-20805" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div data-id="element-20806">
            <h4 className="font-bold text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-pink-300 dark:to-purple-200" data-id="element-20807">
              {data.stage}
            </h4>
            <p className="text-sm text-gray-600 dark:text-indigo-200/70" data-id="element-20808">
              {data.current}/{data.nextLevel} XP to level {data.level + 1}
            </p>
            <div className="mt-2 flex items-center gap-2" data-id="element-20809">
              <div className="w-32 h-2 bg-pink-100 dark:bg-pink-900/30 rounded-full overflow-hidden backdrop-blur-sm" data-id="element-20810">
                <div className="h-full bg-gradient-to-r from-pink-500 to-pink-400 rounded-full relative glow-bar" style={{
                width: `${progressPercentage}%`
              }} data-id="element-20811"></div>
              </div>
              <span className="text-xs text-pink-500 dark:text-pink-300/80" data-id="element-20812">
                {Math.round(progressPercentage)}%
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4" data-id="element-20813">
          <div className="bg-pink-50 dark:bg-pink-900/30 text-pink-500 dark:text-pink-300 px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 border border-pink-200/50 dark:border-pink-500/20 backdrop-blur-sm" data-id="element-20814">
            <Flame size={12} className="text-pink-500 dark:text-pink-400" data-id="element-20815" />
            <span data-id="element-20816">+{data.weeklyEarned} XP this week</span>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-300 px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 border border-purple-200/50 dark:border-purple-500/20 backdrop-blur-sm" data-id="element-20817">
            <span className="text-amber-500 dark:text-amber-400" data-id="element-20818">🔥</span>
            <span data-id="element-20819">{data.streak.daily}-day streak</span>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-300 px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 border border-blue-200/50 dark:border-blue-500/20 backdrop-blur-sm" data-id="element-20820">
            {data.rank.label}
          </div>
        </div>
        <div className="mt-2" data-id="element-20821">
          <div className="flex items-center gap-1 mb-2" data-id="element-20822">
            <span className="text-xs text-gray-600 dark:text-indigo-200/70" data-id="element-20823">
              Weekly streak:
            </span>
          </div>
          <div className="flex gap-1" data-id="element-20824">
            {data.streak.heatmap.map((active, index) => <div key={index} className={`w-8 h-8 rounded-md flex items-center justify-center text-xs border ${active ? 'bg-gradient-to-br from-pink-400/50 to-purple-400/50 dark:from-pink-600/50 dark:to-purple-600/50 text-white border-pink-300/30 dark:border-pink-500/30 shadow-sm dark:shadow-[0_0_8px_rgba(236,72,153,0.3)]' : 'bg-gray-50 dark:bg-gray-900/50 text-gray-400 border-gray-200/50 dark:border-gray-700/50'}`} data-id="element-20825">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'][index]}
              </div>)}
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-purple-200/30 dark:border-indigo-500/20" data-id="element-20826">
          <button className="text-sm text-blue-500 dark:text-blue-300 flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-200 transition-colors" data-id="element-20827">
            <HelpCircle size={14} data-id="element-20828" />
            <span data-id="element-20829">Learn how to earn more XP</span>
          </button>
        </div>
      </div>
    </div>;
};